// Shared per-game ("best of N") result editor used by both the group cell and
// the bracket node dialogs. The hook owns the in-progress draft and derives the
// live games tally / best-of validation; the component renders the game rows.

import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import type { GameScore, GamesValidation, MatchSummary } from '../domain';
import { summarizeMatch, validateGames } from '../domain';
import { useTranslation } from '../state/I18nContext';

const toCount = (value: string) => Math.max(0, Math.trunc(Number(value)) || 0);

/** Drops trailing "0–0" rows so an unfilled game isn't treated as a tie. */
export function cleanGames(rows: readonly GameScore[]): GameScore[] {
  const cleaned = rows.map((g) => ({ ...g }));
  while (cleaned.length > 0) {
    const last = cleaned[cleaned.length - 1];
    if (last.pointsA === 0 && last.pointsB === 0) cleaned.pop();
    else break;
  }
  return cleaned;
}

export interface GamesDraft {
  rows: GameScore[];
  cleaned: GameScore[];
  validation: GamesValidation;
  summary: MatchSummary;
  setRow: (index: number, side: 'A' | 'B', value: number) => void;
  addGame: () => void;
  removeGame: (index: number) => void;
}

/** Draft state for a best-of-N series, reset whenever the dialog (re)opens. */
export function useGamesDraft(
  open: boolean,
  games: readonly GameScore[],
  bestOf: number,
): GamesDraft {
  const [rows, setRows] = useState<GameScore[]>([]);

  useEffect(() => {
    if (!open) return;
    setRows(
      games.length > 0
        ? games.map((g) => ({ ...g }))
        : [{ pointsA: 0, pointsB: 0 }],
    );
  }, [open, games]);

  const cleaned = cleanGames(rows);
  return {
    rows,
    cleaned,
    validation: validateGames(cleaned, bestOf),
    summary: summarizeMatch(cleaned, bestOf),
    setRow: (index, side, value) =>
      setRows((r) =>
        r.map((row, i) =>
          i === index
            ? { ...row, [side === 'A' ? 'pointsA' : 'pointsB']: value }
            : row,
        ),
      ),
    addGame: () =>
      setRows((r) =>
        r.length >= bestOf ? r : [...r, { pointsA: 0, pointsB: 0 }],
      ),
    removeGame: (index) => setRows((r) => r.filter((_, i) => i !== index)),
  };
}

interface GamesEditorProps {
  draft: GamesDraft;
  bestOf: number;
  nameA: string;
  nameB: string;
}

export function GamesEditor({ draft, bestOf, nameA, nameB }: GamesEditorProps) {
  const { t } = useTranslation();
  const { rows, summary, validation, setRow, addGame, removeGame } = draft;
  const canAdd = rows.length < bestOf && !summary.complete;

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="subtitle2" noWrap sx={{ flex: 1 }}>
          {nameA}
        </Typography>
        <Typography variant="subtitle2" sx={{ width: 64, textAlign: 'center' }}>
          {summary.gamesA}–{summary.gamesB}
        </Typography>
        <Typography variant="subtitle2" noWrap sx={{ flex: 1, textAlign: 'right' }}>
          {nameB}
        </Typography>
      </Box>

      <Stack spacing={1}>
        {rows.map((row, index) => (
          <Box
            key={index}
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ width: 24 }}
            >
              {t('admin.scoresheet.gameHeader', { n: index + 1 })}
            </Typography>
            <TextField
              type="number"
              size="small"
              aria-label={t('admin.groups.gamesEditor.pointsFor', {
                n: index + 1,
                name: nameA,
              })}
              value={row.pointsA}
              onChange={(e) => setRow(index, 'A', toCount(e.target.value))}
              sx={{ flex: 1 }}
              slotProps={{ htmlInput: { min: 0 } }}
            />
            <Typography color="text.secondary">–</Typography>
            <TextField
              type="number"
              size="small"
              aria-label={t('admin.groups.gamesEditor.pointsFor', {
                n: index + 1,
                name: nameB,
              })}
              value={row.pointsB}
              onChange={(e) => setRow(index, 'B', toCount(e.target.value))}
              sx={{ flex: 1 }}
              slotProps={{ htmlInput: { min: 0 } }}
            />
            <IconButton
              aria-label={t('admin.groups.gamesEditor.removeGame', { n: index + 1 })}
              size="small"
              onClick={() => removeGame(index)}
              disabled={rows.length <= 1}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Box>
        ))}
      </Stack>

      <Button
        size="small"
        startIcon={<AddIcon />}
        onClick={addGame}
        disabled={!canAdd}
        sx={{ mt: 1 }}
      >
        {t('admin.groups.gamesEditor.addGame')}
      </Button>

      {!validation.valid && (
        <Typography
          variant="caption"
          color="warning.main"
          sx={{ display: 'block', mt: 1 }}
        >
          {validation.code
            ? t(
                `admin.groups.gamesEditor.invalid.${validation.code}`,
                validation.params,
              )
            : validation.reason}
        </Typography>
      )}
    </Box>
  );
}
